/**
 * PayPal Client Service
 * Handles client-side PayPal payment operations
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, onSnapshot, Unsubscribe } from '../services/firestoreCompat';
import { db, HAS_VALID_FIREBASE_CONFIG } from './firebaseConfig';

const NOT_CONFIGURED_ERR = {
  success: false,
  error: 'PayPal payments require Firebase Functions — feature not configured in this build.',
};

interface PayPalOrderResult {
  success: boolean;
  orderId?: string;
  approvalUrl?: string;
  status?: string;
  error?: string;
}

interface PayPalCaptureResult {
  success: boolean;
  orderId?: string;
  status?: string;
  transactionId?: string;
  amount?: number;
  error?: string;
}

class PayPalService {
  private static instance: PayPalService;

  private constructor() {}

  public static getInstance(): PayPalService {
    if (!PayPalService.instance) {
      PayPalService.instance = new PayPalService();
    }
    return PayPalService.instance;
  }

  /**
   * Get Firebase Functions instance
   */
  private getFunctions() {
    return getFunctions();
  }

  /**
   * Create PayPal order and get approval URL
   * @param orderData - Order data
   */
  public async createOrder(orderData: {
    amount: number;
    orderId: string;
    currency?: string;
    description?: string;
  }): Promise<PayPalOrderResult> {
    if (!HAS_VALID_FIREBASE_CONFIG) return NOT_CONFIGURED_ERR;
    try {
      const functions = this.getFunctions();
      const createOrder = httpsCallable(functions, 'paypalCreateOrder');

      const result = await createOrder({
        amount: orderData.amount,
        orderId: orderData.orderId,
        currency: orderData.currency || 'EUR',
        description: orderData.description,
      });

      const data = result.data as any;
      
      console.log('✅ [PAYPAL] Order creation response:', {
        success: data.success,
        orderId: data.orderId,
        hasApprovalUrl: !!data.approvalUrl,
        status: data.status,
        error: data.error,
      });

      if (data.success) {
        console.log('✅ [PAYPAL] PayPal order created successfully:', {
          paypalOrderId: data.orderId,
          approvalUrl: data.approvalUrl?.substring(0, 50) + '...',
          status: data.status,
        });
        return {
          success: true,
          orderId: data.orderId,
          approvalUrl: data.approvalUrl,
          status: data.status,
        };
      } else {
        console.error('❌ [PAYPAL] PayPal order creation failed:', data.error);
        throw new Error(data.error || 'Failed to create PayPal order');
      }
    } catch (error: any) {
      console.error('❌ [PAYPAL] Error creating order:', error);
      return {
        success: false,
        error: error.message || 'Failed to create PayPal order',
      };
    }
  }

  /**
   * Capture PayPal order (after customer approval)
   * @param paypalOrderId - PayPal order ID
   */
  public async captureOrder(paypalOrderId: string): Promise<PayPalCaptureResult> {
    if (!HAS_VALID_FIREBASE_CONFIG) return NOT_CONFIGURED_ERR;
    try {
      const functions = this.getFunctions();
      const captureOrder = httpsCallable(functions, 'paypalCaptureOrder');

      const result = await captureOrder({ paypalOrderId });

      const data = result.data as any;

      if (data.success) {
        return {
          success: true,
          orderId: data.orderId,
          status: data.status,
          transactionId: data.transactionId,
          amount: data.amount,
        };
      } else {
        throw new Error(data.error || 'Failed to capture PayPal order');
      }
    } catch (error: any) {
      console.error('❌ [PAYPAL] Error capturing order:', error);
      return {
        success: false,
        error: error.message || 'Failed to capture PayPal order',
      };
    }
  }

  /**
   * Listen to order payment status changes in real-time
   * @param orderId - Firebase order ID
   * @param callback - Callback function called when status changes
   * @returns Unsubscribe function
   */
  public listenToPaymentStatus(
    orderId: string,
    callback: (status: 'pending' | 'completed' | 'failed' | 'refunded') => void
  ): Unsubscribe {
    const orderRef = doc(db, 'orders', orderId);

    return onSnapshot(orderRef, (snapshot) => {
      if (snapshot.exists()) {
        const orderData = snapshot.data();
        const paymentStatus = orderData.payment_status || 'pending';
        console.log('📡 [PAYPAL] Payment status updated:', paymentStatus);
        callback(paymentStatus as 'pending' | 'completed' | 'failed' | 'refunded');
      }
    }, (error) => {
      console.error('❌ [PAYPAL] Error listening to payment status:', error);
    });
  }

  /**
   * Listen to PayPal order status changes
   * @param paypalOrderId - PayPal order ID (document ID in paypal_orders collection)
   * @param callback - Callback function called when status changes
   * @returns Unsubscribe function
   */
  public listenToPayPalOrderStatus(
    paypalOrderId: string,
    callback: (status: string) => void
  ): Unsubscribe {
    // Note: paypalOrderId is the document ID in paypal_orders collection
    const paypalOrderRef = doc(db, 'paypal_orders', paypalOrderId);
    
    return onSnapshot(paypalOrderRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        const status = data.status || 'CREATED';
        console.log('📡 [PAYPAL] Order status updated:', status);
        callback(status);
      }
    }, (error) => {
      console.error('❌ [PAYPAL] Error listening to PayPal order status:', error);
    });
  }
}

export default PayPalService;

